import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { ConfirmDialog, type ConfirmState } from '../../components/ui/confirm';
import { ActionMenu } from '../../components/ui/action-menu';
import { PaginationControls, useControlledPagination } from '../../components/ui/pagination';
import { SearchBox } from '../../components/ui/search';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/state';
import { Table, Td, Th } from '../../components/ui/table';
import { Input, Label } from '../../components/ui/field';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../../shared/api';
import { copy, labelForStaffRole, labelForStaffStatus } from '../../shared/i18n';
import { useUrlListFilters } from '../../shared/listFilters';
import { matchesSearch } from '../../shared/search';

const staffRoles = ['Administrator', 'Receptionist', 'Staff'] as const;

export function UsersPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const defaults = useMemo(() => ({ q: '', page: '1', pageSize: '10' }), []);
  const { values: filters, setValue } = useUrlListFilters(defaults);
  const search = filters.q;
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [masterCode, setMasterCode] = useState('');
  const page = Math.max(1, Number(filters.page) || 1);
  const pageSize = Math.max(10, Number(filters.pageSize) || 10);
  const isAdministrator = user?.groups.includes('Administrator') ?? false;
  const userListOptions = { limit: page * pageSize + 1, q: search };
  const usersQuery = useQuery({ queryKey: ['staff-users', userListOptions], queryFn: () => api.staffUsers.list(userListOptions), enabled: isAdministrator });
  const rolesQuery = useQuery({ queryKey: ['staff-roles'], queryFn: api.staffRoles.list, enabled: isAdministrator });
  const masterCodeQuery = useQuery({ queryKey: ['master-code-settings'], queryFn: api.masterCodeSettings.get, enabled: isAdministrator });
  const saveMasterCode = useMutation({
    mutationFn: (value: string | null) => api.masterCodeSettings.update(value),
    onSuccess: async () => { setMasterCode(''); await queryClient.invalidateQueries({ queryKey: ['master-code-settings'] }); },
  });
  const users = usersQuery.data?.items ?? [];
  const roles = rolesQuery.data?.items.length ? rolesQuery.data.items : staffRoles;
  const deleteRole = useMutation({
    mutationFn: api.staffRoles.remove,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['staff-roles'] });
      await queryClient.invalidateQueries({ queryKey: ['staff-users'] });
    },
  });
  const confirmDeleteRole = (role: string) => {
    const assignedCount = users.filter((staffUser) => staffUser.roles.includes(role)).length;
    const detail = assignedCount > 0 ? ` Este rol esta asignado a ${assignedCount} usuario(s).` : '';
    setConfirm({
      title: 'Eliminar rol',
      body: `Eliminar el rol "${role}"?${detail}`,
      confirmLabel: 'Eliminar',
      onConfirm: () => deleteRole.mutate(role),
    });
  };
  const filteredUsers = useMemo(
    () => users.filter((staffUser) => matchesSearch(search, [staffUser.email, staffUser.username, staffUser.status, staffUser.enabled ? copy.common.active : copy.common.disabled, ...staffUser.roles.map(labelForStaffRole)])),
    [search, users],
  );
  const usersPage = useControlledPagination(filteredUsers, page, (nextPage) => setValue('page', String(nextPage)), pageSize);

  if (!isAdministrator) {
    return <ErrorState message={copy.users.adminRequired} />;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">{copy.users.title}</h1>
        <p className="mt-1 text-sm text-slate-500">{copy.users.body}</p>
      </div>

      <section className="grid gap-3 md:grid-cols-3">
        {roles.map((role) => {
          const count = users.filter((staffUser) => staffUser.roles.includes(role)).length;
          return (
            <Card key={role}>
              <CardContent className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-normal text-slate-500">Rol</p>
                  <p className="mt-1 text-lg font-semibold text-slate-950">{labelForStaffRole(role)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#eef3ea] text-[#596a52]">{count} usuario(s)</Badge>
                  <Button variant="danger" className="h-8 w-8 px-0" disabled={deleteRole.isPending} aria-label={`Eliminar rol ${role}`} title={`Eliminar rol ${role}`} onClick={() => confirmDeleteRole(role)}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </section>

      <Card>
        <CardHeader><div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-[#75816a]" aria-hidden="true" /><h2 className="text-sm font-semibold">Código maestro</h2></div></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-[#687461]">Protege la edición y eliminación de pagos. El código se almacena cifrado y nunca se vuelve a mostrar.</p>
          <Badge className={masterCodeQuery.data?.configured ? 'bg-[#eef3ea] text-[#2f6b38]' : 'bg-[#f3e5df] text-[#8a4a3a]'}>{masterCodeQuery.data?.configured ? 'Configurado' : 'Sin configurar'}</Badge>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="grid flex-1 gap-1.5"><Label htmlFor="masterCode">Nuevo código maestro</Label><Input id="masterCode" type="password" value={masterCode} onChange={(event) => setMasterCode(event.target.value)} minLength={4} autoComplete="new-password" /></div>
            <Button type="button" disabled={masterCode.length < 4 || saveMasterCode.isPending} onClick={() => saveMasterCode.mutate(masterCode)}>Guardar código</Button>
            <Button type="button" variant="danger" disabled={!masterCodeQuery.data?.configured || saveMasterCode.isPending} onClick={() => saveMasterCode.mutate(null)}>Quitar código</Button>
          </div>
          {masterCodeQuery.isError ? <ErrorState message={masterCodeQuery.error.message} /> : null}
          {saveMasterCode.isError ? <ErrorState message={saveMasterCode.error.message} /> : null}
        </CardContent>
      </Card>

      <div className="flex flex-wrap justify-start gap-2">
        <Link className="inline-flex h-10 items-center gap-2 rounded-md border border-[#d6ded0] bg-white px-4 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to="/admin/users/roles/new">
          <Plus className="h-4 w-4" aria-hidden="true" />
          Nuevo rol
        </Link>
        <Link className="inline-flex h-10 items-center gap-2 rounded-md border border-[#d6ded0] bg-white px-4 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to="/admin/users/new">
          <Plus className="h-4 w-4" aria-hidden="true" />
          {copy.users.inviteUser}
        </Link>
        <Link className="inline-flex h-10 items-center gap-2 rounded-md border border-[#d6ded0] bg-white px-4 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to="/admin/users/permissions">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          Permisos por modulo
        </Link>
      </div>

      <section>
        <Card>
          <CardHeader>
            <div className="flex flex-col items-start gap-3">
              <h2 className="text-sm font-semibold">{copy.users.directory}</h2>
              <SearchBox value={search} onChange={(value) => setValue('q', value)} placeholder="Buscar usuario" label="Buscar usuario" />
            </div>
          </CardHeader>
          <CardContent>
            {usersQuery.isLoading ? <LoadingState label={copy.users.loading} /> : null}
            {usersQuery.isError ? <ErrorState message={usersQuery.error.message} /> : null}
            {rolesQuery.isError ? <ErrorState message={rolesQuery.error.message} /> : null}
            {deleteRole.isError ? <ErrorState message={deleteRole.error.message} /> : null}
            {usersQuery.data && usersQuery.data.items.length === 0 ? <EmptyState title={copy.users.emptyTitle} body={copy.users.emptyBody} /> : null}
            {usersQuery.data && usersQuery.data.items.length > 0 && filteredUsers.length === 0 ? <EmptyState title="Sin resultados" body="Intenta con otro usuario, rol o estado." /> : null}
            {usersQuery.data && filteredUsers.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <thead>
                    <tr>
                      <Th>{copy.common.user}</Th>
                      <Th>{copy.common.role}</Th>
                      <Th>{copy.common.status}</Th>
                      <Th>{copy.common.actions}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersPage.pageItems.map((staffUser) => {
                      const currentRole = staffUser.roles[0] ?? 'Staff';
                      return (
                        <tr key={staffUser.username}>
                          <Td className="font-medium text-slate-900">{staffUser.email ?? staffUser.username}</Td>
                          <Td>
                            <div className="flex items-center gap-2">
                              <ShieldCheck className="h-4 w-4 text-[#75816a]" aria-hidden="true" />
                              <span>{labelForStaffRole(currentRole)}</span>
                            </div>
                          </Td>
                          <Td>
                            <Badge className={staffUser.enabled ? 'bg-[#eef3ea] text-[#596a52]' : 'bg-[#f3e5df] text-[#a94f40]'}>
                              {staffUser.enabled ? labelForStaffStatus(staffUser.status) : copy.common.disabled}
                            </Badge>
                          </Td>
                          <Td>
                            <ActionMenu
                              groups={[
                                {
                                  label: 'Gestion',
                                  children: (
                                    <Link className="flex items-center gap-2 rounded-md px-2 py-2 text-[#334033] hover:bg-[#eef3ea]" to={`/admin/users/${encodeURIComponent(staffUser.username)}/edit`}>
                                      <Pencil className="h-4 w-4" aria-hidden="true" />
                                      Editar
                                    </Link>
                                  ),
                                },
                              ]}
                            />
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : null}
            <PaginationControls {...usersPage} onPageChange={usersPage.setPage} onPageSizeChange={(nextPageSize) => setValue('pageSize', String(nextPageSize))} />
          </CardContent>
        </Card>
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
